import { FieldError, useFormContext, useWatch } from 'react-hook-form'
import { Skeleton, Text } from '@lounge-fe/ui-kit'
import SelectGroup from '../SelectGroup'
import classNames from 'classnames'
import { isOptionInStock } from '../utils'
import { VariantOptionsProps } from './types'
import { GiftCardDesignsCarousel } from '../GiftCardDesignsCarousel/GiftCardDesignsCarousel'
import { DESIGNS_NAME } from '../hooks/useGiftCard'
import { SizeGuideModal } from '../SizeGuideModal'
import { useDataTranslation } from '@/features/localisation/hooks/useDataTranslation/useDataTranslation'
import { useTranslatedValue } from '../hooks/useTranslatedValue'

export const VariantSelection = ({
  options,
  variants,
  getIsSelectOption,
  isDisabledSelectable,
  sizeGuides,
  sizeGuidePrimaryAction,
  sizeGuideSecondaryAction,
}: VariantOptionsProps) => {
  const {
    formState: { errors },
  } = useFormContext<{ options: FieldError[] }>()

  const selectedOptions = useWatch({ name: 'options' })

  const { translateValue } = useTranslatedValue()
  const { t } = useDataTranslation()

  return (
    <div
      data-testid="variant-selection"
      className={classNames('flex flex-wrap gap-4 relative')}
    >
      {sizeGuides && (
        <SizeGuideModal
          buttonClassName="absolute top-0 right-0 leading-sm"
          sizeGuides={sizeGuides}
          primaryAction={sizeGuidePrimaryAction}
          secondaryAction={sizeGuideSecondaryAction}
        />
      )}
      {options.map(({ name, values, label }, idx) => {
        const optionName = label ?? name
        const isSelectOption = getIsSelectOption(optionName)

        const title = translateValue({
          preferredValue: label ?? name,
          fallbackValue: name,
          loadingState: (
            <Skeleton>
              <Skeleton.Block className="w-full max-w-[150px] h-5" />
            </Skeleton>
          ),
        })

        if (name.toLowerCase() === DESIGNS_NAME) {
          return (
            <GiftCardDesignsCarousel
              key={name}
              name={`options.${idx}`}
              options={values}
              renderTitle={({ currentOption }) => (
                <Text variant="body-lg" className="mb-1">
                  <b className="font-regular">{title}:</b> {currentOption}
                </Text>
              )}
              variants={variants}
              index={idx}
              selectedId={selectedOptions[idx]}
            />
          )
        }

        const isEvenSelect = isSelectOption && idx % 2 === 0

        return (
          <SelectGroup
            key={name}
            className={
              isSelectOption ? 'flex-[1_0_calc(50%-16px)]' : 'flex-[1_0_100%]'
            }
            title={isSelectOption ? t('product.select_size') : title}
            showTitle={isSelectOption ? isEvenSelect : true}
            fields={[
              {
                label: title,
                name: `options.${idx}`,
                isDisabledSelectable,
                testId: `group-${name.toLowerCase().replaceAll(' ', '-')}`,
                options: values.map((option) => ({
                  label: option,
                  value: option,
                  disabled: !isOptionInStock(
                    option,
                    idx,
                    selectedOptions,
                    variants,
                  ),
                })),
                errors,
                control: isSelectOption ? 'select' : 'choicechip',
              },
            ]}
          />
        )
      })}
    </div>
  )
}
